import { CategoryOpenMode } from '../../../models/category/category';
import { HookTypes } from '../../../models/category/HookTypes';
import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';
import DictionaryService from '../../../services/dictionary/DictionaryService';
import IDictionaryService from '../../../services/dictionary/IDictionaryService';
import { TreeFormFieldNames } from '../../../services/form/schema-factory/TreeFormSchemaFactory';
import { DictionaryTreeFormControlViewModel } from '../../../services/form/view-model/FormControlViewModel';
import { PredefinedForms } from '../../../services/form/view-model/FormCreateViewModel';
import { FormSourceType } from '../../../services/form/view-model/FormSource';
import { getCatalogsStateEntity } from '../../selectors/catalogs';
import {
  getCategoryOpenMode,
  getCategoryState,
} from '../../selectors/category';
import {
  filterDatasetsForCategory,
  getDatasetCategoryId,
} from '../../selectors/dataset';
import { getDictionaryById } from '../../selectors/dictionary';
import { getControlValue, getFormState } from '../../selectors/form';
import { getHierarchyStateEntity } from '../../selectors/hierarchy';
import actions from '../../slices/actions';
import catalogs, { CatalogsStateEntity } from '../../slices/catalogs';
import category from '../../slices/category';
import categoryHook from '../../slices/categoryHook';
import commands from '../../slices/commands';
import dictionary, {
  DictionaryCloseReason,
  DictionaryEffectType,
  DictionaryType,
} from '../../slices/dictionary';
import { CategoryTreeNode } from '../../slices/dm-trees/CategoryTreeNode';
import docLinks from '../../slices/docLinks';
import form, { FormParentType } from '../../slices/form';
import workTable from '../../slices/workTable';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const dictionaryListener = createAppListener();

const dictionaryService: IDictionaryService = new DictionaryService();

dictionaryListener.startListening(
  apiCall(
    {
      request: dictionary.updateOutputs,
      success: dictionary.updateOutputsSuccess,
      failure: dictionary.updateOutputsFailure,
    },
    async ({ id }, state) => {
      const { type, effect } = getDictionaryById(state, id);

      if (type === DictionaryType.CATEGORY) {
        const categoryState = getCategoryState(state, id);
        const datasets = filterDatasetsForCategory(state, id);
        const multiSelect = getDictionaryById(state, id).multiSelect;

        const outputs = await dictionaryService.getOutputs(
          categoryState,
          datasets,
          !!multiSelect
        );

        return { id, outputs };
      } else if (
        type === DictionaryType.TREE &&
        effect.type === DictionaryEffectType.UPDATE_WITH_OUT_PARAMS
      ) {
        const { controls } = getFormState(state, effect.form);
        const selectedId = getControlValue(
          state,
          id,
          TreeFormFieldNames.TARGET_ID
        );
        const { treeType } = getCategoryState(state, id);
        const { nodes } =
          (treeType === CategoryTreeType.CATALOGS &&
            getCatalogsStateEntity(state, id)) ||
          (treeType === CategoryTreeType.HIERARCHY &&
            getHierarchyStateEntity(state, id)) ||
          ({} as CatalogsStateEntity);
        const selected = nodes.find(({ id: nodeId }) => nodeId === selectedId);
        const targetControl = controls[
          effect.field
        ] as DictionaryTreeFormControlViewModel;

        if (!targetControl.outParams) {
          throw new Error('outParams is required for DictTreeInput');
        }

        return {
          id,
          outputs: Object.entries(targetControl.outParams).reduce(
            (memo, [sourceField]) => ({
              ...memo,
              [sourceField]: selected?.[sourceField as keyof CategoryTreeNode],
            }),
            {}
          ),
        };
      }

      throw new Error('Unsupported dictionary to update with out params');
    }
  )
);

dictionaryListener.startListening(
  postDispatch(
    dictionary.close,
    (
      { id, reason = DictionaryCloseReason.COMMON },
      state,
      dispatch,
      prevState
    ) => {
      const { effect, type } = getDictionaryById(prevState, id);

      dispatch(
        category.close({
          categoryId: id,
          save: type === DictionaryType.CATEGORY,
          reason,
        })
      );

      if (
        effect.type === DictionaryEffectType.PROCESS_ACTION &&
        effect.hook &&
        reason === DictionaryCloseReason.CANCEL
      ) {
        dispatch(categoryHook.cancel(effect));
      }

      if (effect.type === DictionaryEffectType.REFRESH_ON_CLOSE) {
        const {
          dataset,
          refresh: { refreshRules },
        } = effect;
        if (refreshRules) {
          dispatch(
            category.refreshWithRules({
              datasetId: dataset,
              refreshRules,
            })
          );
        }
      }

      if ('dataset' in effect) {
        const categoryId = getDatasetCategoryId(state, effect.dataset);
        const openMode = getCategoryOpenMode(state, categoryId);
        if (openMode === CategoryOpenMode.HIDDEN) {
          dispatch(category.close({ categoryId }));
        }
      }
    }
  )
);

dictionaryListener.startListening(
  postDispatch(dictionary.submit, ({ id }, state, dispatch) => {
    const { effect, outputs } = getDictionaryById(state, id);
    if (!outputs) {
      return;
    }

    switch (effect.type) {
      case DictionaryEffectType.CALL_BACK:
        effect.callback?.(outputs);
        break;

      case DictionaryEffectType.PROCESS_ACTION:
        dispatch(
          actions.processShowCategory({
            outParams: outputs,
            action: effect.action,
            dataset: effect.dataset,
            category: effect.category,
            dictionaryId: id,
            hook: effect.hook,
            refresh: effect.refresh?.refresh,
            refreshRules: effect.refresh?.refreshRules,
          })
        );
        return;

      case DictionaryEffectType.EXECUTE_COMMAND:
        dispatch(
          commands.execute({
            datasetId: effect.dataset,
            command: effect.command,
            args: outputs.id,
            formId: undefined,
            hook: effect.hook ? HookTypes.OPEN : undefined,
          })
        );
        break;

      case DictionaryEffectType.UPDATE_WITH_OUT_PARAMS:
        dispatch(
          form.updateWithOutParams({
            formId: effect.form,
            field: effect.field,
            value: outputs,
            skipNextUpdater: false,
          })
        );
        break;

      case DictionaryEffectType.UPDATE_FORM:
        dispatch(
          form.update({
            formId: effect.form,
            field: effect.field,
            value: outputs,
          })
        );
        break;

      case DictionaryEffectType.WORK_TABLE:
        dispatch(
          workTable.updateWorkDays({
            selectedDays: effect.selectedDays,
            tableId: effect.tableId,
            outParams: outputs as ReturnType<
              typeof workTable.updateWorkDays
            >['payload']['outParams'],
          })
        );
        break;

      case DictionaryEffectType.WORK_HOURS:
        dispatch(
          workTable.createWorkHours({
            workerId: effect.tableId,
            outParams: outputs as ReturnType<
              typeof workTable.createWorkHours
            >['payload']['outParams'],
          })
        );
        break;

      case DictionaryEffectType.DOC_LINKS:
        dispatch(
          docLinks.openDocs({
            categoryName: outputs.category,
            docId: effect.docId,
            sourceCategoryName: effect.categoryName,
            mode: effect.mode,
          })
        );
        break;
    }

    dispatch(dictionary.close({ id }));
  })
);

dictionaryListener.startListening(
  postDispatch(dictionary.open, ({ id, type }, state, dispatch) => {
    if (type === DictionaryType.TREE) {
      const { title } = getCategoryState(state, id);

      dispatch(catalogs.linkWithForm({ catalogId: id, formId: id }));
      dispatch(
        form.create({
          type: FormSourceType.PREDEFINED,
          subtype: PredefinedForms.TREE,
          category: id,
          treeId: id,
          formId: id,
          command: '',
          parent: { type: FormParentType.DICTIONARY, id },
          caption: title,
        })
      );
    }
  })
);

export default dictionaryListener.middleware;
